import { sha256Hex, stableJson } from "./hash";
import { formatMoney } from "./money";
import type { Money } from "./types";

export const BUYER_FUNDING_ACK_ESIGN_LIMITATION =
  "This records acknowledgment of the instruction version reviewed. It does not verify identity for Veto, confirm source account ownership, confirm good funds, validate the escrow destination, or approve reliance on funds.";

export type AcknowledgmentDocumentInput = {
  fileCode: string;
  propertyAddress: string;
  buyerName: string;
  buyerEmail?: string;
  officeName: string;
  instructionVersion: string;
  instructionPublishedAt: string;
  amountDue: Money;
  fundingPurpose: string;
  instructionsViewedAt: string;
};

export type AcknowledgmentDocument = {
  instructionHash: string;
  documentHash: string;
  plainText: string;
  html: string;
};

export function instructionVersionLabel(fileCode: string, versionLabel: string): string {
  return `${fileCode.trim().toUpperCase()}-v${versionLabel}`;
}

export async function buildAcknowledgmentDocument(
  input: AcknowledgmentDocumentInput
): Promise<AcknowledgmentDocument> {
  const versionId = instructionVersionLabel(input.fileCode, input.instructionVersion);
  const instructionHash = await sha256Hex(
    stableJson({
      fileCode: input.fileCode,
      versionLabel: input.instructionVersion,
      versionId,
      publishedAt: input.instructionPublishedAt,
      beneficiary: input.officeName,
      amountCents: input.amountDue.amountCents
    })
  );

  const plainText = buildPlainText({ ...input, versionId, instructionHash });
  const html = buildHtml({ ...input, versionId, instructionHash });
  const documentHash = await sha256Hex(html);

  return { instructionHash, documentHash, plainText, html };
}

function buildPlainText(
  input: AcknowledgmentDocumentInput & { versionId: string; instructionHash: string }
): string {
  return `Buyer Funding Acknowledgment

File: ${input.fileCode}
Property: ${input.propertyAddress}
Buyer: ${input.buyerName}
Office: ${input.officeName}
Instruction version: ${input.versionId}
Instruction hash: ${input.instructionHash}
Amount requested: ${formatMoney(input.amountDue)}
Funding purpose: ${input.fundingPurpose}
Date/time viewed: ${input.instructionsViewedAt}

I reviewed the office-published funding instructions for this file.

I understand that if I receive different instructions by email, text, phone, or any other channel, I should stop and contact the escrow office using an office-known contact path.

I understand this acknowledgment records what I reviewed. It does not confirm that funds were sent, received, matched, applied, or safe to rely on.

Veto records the review. The office decides.`;
}

function buildHtml(
  input: AcknowledgmentDocumentInput & { versionId: string; instructionHash: string }
): string {
  const body = buildPlainText(input)
    .split("\n\n")
    .map((block) => `<p>${escapeHtml(block.replace(/\n/g, "<br/>"))}</p>`)
    .join("\n");

  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="utf-8"/><title>Buyer Funding Acknowledgment</title>
<style>
  body { font-family: Georgia, serif; font-size: 12pt; line-height: 1.45; color: #191919; max-width: 640px; margin: 24px; }
  h1 { font-size: 16pt; margin-bottom: 8px; }
  .meta { font-size: 10pt; color: #596158; }
  .boundary { margin-top: 16px; padding: 12px; border: 1px solid #d8d2c4; background: #fffdf8; }
</style>
</head>
<body>
<h1>Buyer Funding Acknowledgment</h1>
<p class="meta">Instruction version ${escapeHtml(input.versionId)} · hash ${escapeHtml(input.instructionHash)}</p>
${body}
<div class="boundary"><strong>Limitation:</strong> ${escapeHtml(BUYER_FUNDING_ACK_ESIGN_LIMITATION)}</div>
</body>
</html>`;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
